/*
   UniqueArray is equivalent of a set with indexing semantics
*/
export class UniqueArray<T> extends Array<T> {

  constructor(...values: T[]) {
    super(...values);
    Object.setPrototypeOf(this, UniqueArray.prototype);
  }

  public static createUniqueArray<T>(...values: T[]): UniqueArray<T> {
    return new UniqueArray(...values);
  }

  public remove(item: T): void {
    const index = this.indexOf(item);
    if (index === -1) {
      throw new Error('No such item found');
    }
    this.splice(index, 1);
  }

  public push(...items: T[]): number {
    this.checkValidity(...items);
    return super.push(...items);
  }

  public fill(value: T, start?: number, end?: number): this {
    // eslint-disable-next-line no-unused-expressions,  @typescript-eslint/no-unused-expressions
    value; start; end;
    throw new Error('Cannot fill with same value');
  }

  public unshift(...items: T[]): number {
    this.checkValidity(...items);
    return super.unshift(...items);
  }

  /*
    Moves items starting at start to end-1 by position(s). Start has to be > 0.
    Useful for item reordering in datasets/parameters
  */
  public moveUp(start: number, end: number = start + 1, positions: number = 1): void {
    if (start === 0 || end - 1 < start || start < positions || end > this.length) {
      throw new Error('Invalid parameters for moveup');
    }
    // We can repeatedly slide over with position = 1 without extra storage.
    // Lets just copy over the slice that will get shuffled
    const slice = this.slice(start - positions, start);
    for (let index = start; index < end; index++) {
      this[index - positions] = this[index];
    }

    for (let index = 0; index < slice.length; index++) {
      this[end - positions + index] = slice[index];
    }
  }

  /*
    Moves items starting at start to end-1 by position(s).
    Useful for item reordering in datasets/parameters
  */
  public moveDown(start: number, end: number = start + 1, positions: number = 1): void {
    if (end - 1 < start || end + positions > this.length) {
      throw new Error('Invalid parameters for moveDown');
    }
    // We can repeatedly slide over with position = 1 without extra storage.
    // Lets just copy over the slice that will get shuffled
    const slice = this.slice(end, end + positions);
    for (let index = end - 1; index >= start; index--) {
      this[index + positions] = this[index];
    }

    for (let index = 0; index < slice.length; index++) {
      this[start + index] = slice[index];
    }
  }

  private checkValidity(...items: T[]): void {
    for (let index = 0; index < items.length; index++) {
      const item = items[index];
      if (null == item) {
        throw new Error('Cannot add null or undefined items');
      }
      if (this.indexOf(item) !== -1) {
        throw new Error('Item already exists');
      }
      if (items.indexOf(item, index + 1) !== -1) {
        throw new Error('Duplicate items in list');
      }
    }
  }
}
